import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import LazyHydrate from 'react-lazy-hydration';
import React, { MouseEventHandler, PropsWithChildren, useCallback, useMemo } from 'react';
import { SxProps } from '@cp/ds/index';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { CITY_PATH_PARAMETER, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { DEFAULT_CATALOG_PER_PAGE } from '@common/const/catalog';
import Card from '@cp/ds/src/components/card';
import { coreCatalogApi } from '@cp/entities/catalog';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { useOpenCatalogItem } from 'src/features/catalogItem/view';
import { generateCatalogItemPermalink } from 'src/entities/catalog/item';
import { CatalogFlexList, CatalogListItem, EmptyCatalog, ICatalogItem } from 'src/entities/catalog';
import { setFakeOnlineStatus } from 'src/widgets/catalog';
import { CatalogListLinksSchema } from './listLinksSchema';

const ITEM_SPACING_DEFAULT = 2.5;

const getKey = (data: CatalogItemViewModel) => {
  return [data.type, data.careerId, data.id].map(String).join('-');
};

export interface CatalogListProps {
  sx?: SxProps;
  request: CatalogItemListRequest;
  itemWidth?: React.CSSProperties['width'];
  itemSpacing?: React.CSSProperties['width'];
  banners?: {
    [PositionIndex: number]: React.ReactNode;
  };
  CatalogItem: React.ComponentType<ICatalogItem>;
  FallbackCard?: React.ComponentType;
  ListEntity?: React.ComponentType<PropsWithChildren & { sx?: SxProps }>;
}

const DefaultFallback = () => (
  <Card sx={{ backgroundColor: 'white', borderRadius: '24px', width: '100%' }}>
    <ContentSkeleton blocks={1} sx={{ margin: 1.5 }} />
  </Card>
);

export const CatalogList = ({
  sx,
  request,
  itemWidth,
  itemSpacing = ITEM_SPACING_DEFAULT,
  banners = {},
  CatalogItem,
  FallbackCard = DefaultFallback,
  ListEntity = CatalogFlexList,
}: CatalogListProps) => {
  const isMobile = useIsMobile();
  const { data, isLoading } = coreCatalogApi.endpoints.getItems.useQuery(request);
  const generatePath = usePathGenerator();
  const openCatalogItem = useOpenCatalogItem();
  const items = useMemo(() => {
    return isLoading ? new Array<null>(DEFAULT_CATALOG_PER_PAGE).fill(null) : setFakeOnlineStatus(data?.items ?? []);
  }, [data, isLoading]);

  const onItemClick: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      const id = event.currentTarget.dataset.itemid;
      const url = event.currentTarget.getAttribute('href');
      if (!id) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      openCatalogItem(Number(id), url);
    },
    [openCatalogItem],
  );

  const isEmpty = !items.length && !isLoading;

  return (
    <ListEntity
      sx={{
        gap: itemSpacing,
        ...sx,
      }}
    >
      {isEmpty ? (
        <EmptyCatalog />
      ) : (
        <>
          {items[0] !== null && <CatalogListLinksSchema items={items as CatalogItemViewModel[]} />}
          {items.map((item: CatalogItemViewModel | null, index: number) => {
            if (!item) {
              return (
                <CatalogListItem key={index} sx={{ width: itemWidth }}>
                  <FallbackCard />
                </CatalogListItem>
              );
            }

            const href = generateCatalogItemPermalink(item, generatePath, { CITY_PATH_PARAMETER });

            return (
              <React.Fragment key={getKey(item)}>
                {banners[index + 1] ? (
                  <CatalogListItem sx={{ display: 'flex', justifyContent: 'center', width: isMobile ? '100%' : itemWidth }}>
                    {banners[index + 1]}
                  </CatalogListItem>
                ) : null}
                <CatalogListItem sx={{ width: itemWidth }}>
                  <LazyHydrate whenVisible>
                    <CatalogItem data={item} index={index} onClick={onItemClick} url={href} />
                  </LazyHydrate>
                </CatalogListItem>
              </React.Fragment>
            );
          })}
        </>
      )}
    </ListEntity>
  );
};
